import {Controller} from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["value", "show", "checkout", "minusButton", "plusButton", "limitReachedNote", "minRequirementNote"];
  static values = {
    ticketId: Number,
    ticketName: String,
    ticketDay: String,
    ticketPrice: Number,
    absorbFee: Boolean,
    discountedPrice: Number,
    availableTickets: Number,
    discountApplied: Boolean,
    displayPrice: Number,
    purchasedLimitEnabled: { type: Boolean, default: false },
    minTicketsPerOrder: Number,
    maxTicketsPerOrder: Number,
  };

  connect() {
    // Register this ticket's day (blank for non-day-scoped) so the order
    // summary can label per-day lines distinctly (#32).
    if (this.orderSummaryController.ticketDays) {
      this.orderSummaryController.ticketDays[this.ticketIdValue] = this.ticketDayValue;
    }

    const existingTicket = this.orderSummaryController.tickets[this.ticketIdValue];

    if (existingTicket) {
      // Preserve the quantity the buyer already selected. The ticket item is
      // re-rendered (and this controller re-connected) when a promo code is
      // applied/removed via the validate_promo_code turbo-stream; the order
      // summary controller persists across that re-render and still holds the
      // real quantity. update() overwrites this.tickets[ticketId] by id with the
      // new discounted price, so re-using the existing quantity re-prices the
      // line in place — it must NOT be zeroed just because a discount applies
      // (that wiped the cart back to 0 / "Total Free" on every promo apply).
      const existingQuantity = existingTicket.quantity
      const meetsMinRequirement = this.checkMinimumPurchaseRequirement(existingQuantity);

      this.orderSummaryController.update(this.ticketIdValue, this.ticketNameValue, this.ticketPriceValue,
        existingQuantity, this.absorbFeeValue, this.discountedPriceValue, this.discountAppliedValue, this.displayPriceValue,
        meetsMinRequirement, this.purchasedLimitEnabledValue, this.minTicketsPerOrderValue);

      this.showTarget.textContent = existingQuantity
      this.valueTarget.value = existingQuantity
      if (parseInt(this.valueTarget.value) > 0) this.enableDecrementBtn()
    }
    this.enable_ticket_for_submit()
    this.enableDisableDiscountedBundles(this.discountOperationController.remainingDiscountCodesValue)
  }

  enable_ticket_for_submit() {
    let quantity = parseInt(this.valueTarget.value)
    if (quantity > 0) {
      this.valueTarget.removeAttribute("disabled");
    }
  }

  increment() {
    this.updateValue(1);
  }

  decrement() {
    this.updateValue(-1);
  }

  updateValue(delta) {
    let quantity = parseInt(this.valueTarget.value) || 0;
    const newQuantity = quantity + delta

    if (newQuantity < 0 || newQuantity > this.availableTicketsValue) return

    quantity = newQuantity;
    this.showTarget.textContent = quantity;
    this.valueTarget.value = quantity;
    
    // Calculate best discount for this quantity
    const discount = this.calculateDiscount(quantity);
    
    // Check if minimum purchase requirement is met
    const meetsMinRequirement = this.checkMinimumPurchaseRequirement(quantity);
    
    // Update order summary with discount info
    this.orderSummaryController.update(
      this.ticketIdValue, 
      this.ticketNameValue, 
      this.ticketPriceValue, 
      quantity,
      this.absorbFeeValue, 
      discount.amount, // Use calculated discount amount
      discount.type !== 'none', // discountApplied
      this.displayPriceValue,
      meetsMinRequirement, 
      this.purchasedLimitEnabledValue, 
      this.minTicketsPerOrderValue,
      discount.type, // Pass discount type
      discount.id // Pass discount ID
    );

    this.toggleEnabledState(quantity);
    this.updateButtonStates(quantity);
    this.updateMinRequirementNote(meetsMinRequirement, quantity);

    let remainingDiscountCodesValue = this.discountOperationController?.remainingDiscountCodesValue;
    if (this.discountAppliedValue && remainingDiscountCodesValue != 'notPresent') {
      this.discountOperationController.remainingDiscountCodesValue = (delta > 0) ? parseInt(remainingDiscountCodesValue) - 1 : parseInt(remainingDiscountCodesValue) + 1
      this.enableDisableDiscountedBundles(this.discountOperationController.remainingDiscountCodesValue)
    }
  }

  // Calculate the best discount for current quantity
  calculateDiscount(quantity) {
    // Get discount calculator controller if it exists
    const discountCalculator = this.discountCalculatorController;

    if (discountCalculator) {
      return discountCalculator.calculateBestDiscount(quantity);
    }
    
    // Fallback if no discount calculator
    return { 
      type: 'none', 
      amount: 0, 
      id: null, 
      finalPrice: this.ticketPriceValue,
      totalDiscount: 0
    };
  }

  currentValue() {
    return parseInt(this.valueTarget.value, 10) || 0;
  }

  toggleEnabledState(quantity) {
    if (quantity > 0) {
      this.valueTarget.removeAttribute("disabled");
    } else {
      this.valueTarget.setAttribute("disabled", true);
    }
  }

  updateButtonStates(quantity) {
    // Decrement button: disable when quantity is 0
    (quantity <= 0) ? this.disableDecrementBtn() : this.enableDecrementBtn();
    
    // Increment button: disable when reaching available tickets OR max per order limit
    const maxLimit = this.getEffectiveMaxLimit();
    (quantity >= maxLimit) ? this.disableIncrementBtn() : this.enableIncrementBtn();
  }

  getEffectiveMaxLimit() {
    // Return the smaller of availableTickets and maxTicketsPerOrder
    const limits = [this.availableTicketsValue];
    
    if (this.purchasedLimitEnabledValue && this.maxTicketsPerOrderValue > 0) {
      limits.push(this.maxTicketsPerOrderValue);
    }
    
    return Math.min(...limits);
  }

  checkMinimumPurchaseRequirement(quantity) {
    // If purchase limits not enabled, always return true
    if (!this.purchasedLimitEnabledValue) return true;
    
    // If quantity is 0, ticket is being removed - that's okay, just return true
    if (quantity === 0) return true;
    
    // If minimum is set, check if quantity meets it
    if (this.minTicketsPerOrderValue > 0) {
      return quantity >= this.minTicketsPerOrderValue;
    }
    
    // No minimum set, so requirement is met
    return true;
  }

  enableDisableDiscountedBundles(remainingDiscountCodesValue) {
    this.ticketQuantityControllers.forEach(ticketBundle => {
      if (ticketBundle.discountAppliedValue) {
        if (remainingDiscountCodesValue != '' && remainingDiscountCodesValue <= 0) {
          ticketBundle.disableIncrementBtn()
          ticketBundle.limitReachedNoteTarget.classList.remove('d-none')
        } else if (this.availableTicketsValue > 0 ){
          ticketBundle.enableIncrementBtn()
          ticketBundle.limitReachedNoteTarget.classList.add('d-none')
        }
      }
    });
  }

  disableDecrementBtn() {
    this.minusButtonTarget.classList.add('btn-minus')
    this.minusButtonTarget.classList.remove('btn-plus')
    this.minusButtonTarget.setAttribute("disabled", true)
  }

  enableDecrementBtn() {
    this.minusButtonTarget.classList.remove('btn-minus')
    this.minusButtonTarget.classList.add('btn-plus')
    this.minusButtonTarget.removeAttribute("disabled")
  }

  disableIncrementBtn() {
    this.plusButtonTarget.classList.add('btn-minus')
    this.plusButtonTarget.classList.remove('btn-plus')
    this.plusButtonTarget.setAttribute("disabled", true)
  }

  enableIncrementBtn() {
    this.plusButtonTarget.classList.remove('btn-minus')
    this.plusButtonTarget.classList.add('btn-plus')
    this.plusButtonTarget.removeAttribute("disabled")
  }

  updateMinRequirementNote(meetsMinRequirement, quantity) {
    // Only show note if we have the target and purchase limits are enabled
    if (!this.hasMinRequirementNoteTarget || !this.purchasedLimitEnabledValue) return;
    
    // Show note if quantity > 0 but doesn't meet minimum requirement
    if (quantity > 0 && !meetsMinRequirement) {
      this.minRequirementNoteTarget.classList.remove('d-none');
    } else {
      this.minRequirementNoteTarget.classList.add('d-none');
    }
  }

  get orderSummaryController() {
    return this.application.controllers.find((controller) => {
      return controller.context.identifier === "order-summary";
    });
  }

  get ticketQuantityControllers() {
    return this.application.controllers.filter((controller) => {
      return controller.context.identifier === "ticket-quantity";
    });
  }

  get discountOperationController() {
    return this.application.controllers.find((controller) => {
      return controller.context.identifier === "discount-operation";
    });
  }

  get discountCalculatorController() {
    // Find the discount calculator controller for this specific ticket
    return this.application.controllers.find((controller) => {
      return controller.context.identifier === "ticket-discount-calculator" &&
             controller.element === this.element;
    });
  }
}
